import { useCallback, useState } from 'react';

/**
 * The user's pre-emptive choice from the WorkflowDestinationPicker.
 * Set when the user clicks the chat composer's "Save to workflow" button
 * BEFORE sending. Consumed by the post-send save handler so the prompt
 * doesn't fire again — silent save, single toast.
 *
 * Three shapes correspond to the three picker outcomes:
 *   - existing: { workflowId } — add as TemplateDocument under that workflow
 *   - newWorkflow: { newCategory, newWorkflowName } — create both
 *   - cleared: null — no pre-set; post-send prompt should fire as usual
 */
export type WorkflowDestination =
  | { mode: 'existing'; workflowId: string; workflowName: string; categoryName: string; templateName?: string }
  | { mode: 'newWorkflow'; newCategory: string; newWorkflowName: string; templateName?: string; icon?: string }
  | null;

/**
 * Single-source-of-truth state for the pre-emptive destination across
 * the chat session. Cleared automatically when consumed (`consume()`)
 * so the next message starts fresh — pre-sets are intentionally NOT
 * sticky across sends to avoid silent saves to the wrong place after
 * a context shift.
 */
export function useWorkflowDestination(): {
  destination: WorkflowDestination;
  setDestination: (d: WorkflowDestination) => void;
  /** Returns the current destination AND clears it. Use right before
   *  calling the save API so the next send starts unset. */
  consume: () => WorkflowDestination;
  clear: () => void;
} {
  const [destination, setDestination] = useState<WorkflowDestination>(null);

  const consume = useCallback(() => {
    const d = destination;
    setDestination(null);
    return d;
  }, [destination]);

  const clear = useCallback(() => setDestination(null), []);

  return { destination, setDestination, consume, clear };
}
